<script setup lang="ts">
  import FormHeading from '@/components/form/FormHeading.vue'
  import SelectOrganization from '@/components/organization/SelectOrganization.vue'
  import IdfTagList from '@/components/idf-modal/IdfTagList.vue'
  import CarriersSelector from '@/components/idf-modal/CarriersSelector.vue'
  import type { Organization } from '@/types/organization'
  import type { IdfTag } from '@/types/idf-tag'
  import { computed, ref, watch, type Ref } from 'vue'
  import { useJourneyTemplateStore } from '@/store/journey-template.store'
  import type { JourneyFieldErrors } from '@/types/idf-journey-product'
  import type { Nullish } from '@logistiek-zonder-papier/lzp-library'
  import { storeToRefs } from 'pinia'

  const journeyTemplateStore = useJourneyTemplateStore()
  const { journeyTemplate } = storeToRefs(journeyTemplateStore)

  const selectedCarriers: Ref<Organization[]> = ref([])

  const props = defineProps<{
    error?: JourneyFieldErrors | Nullish
    carrierErrorKey?: keyof JourneyFieldErrors
  }>()

  const validationMessage = computed(() => {
    if (!props.error || !Object.keys(props.error).length) return undefined
    return props.error
  })

  const carrierModel = computed<Organization | null>({
    get() {
      return null
    },

    set(newCarrier: Organization | null) {
      if (!newCarrier) return

      const alreadyExists = selectedCarriers.value.filter(carrier => carrier.id == newCarrier.id)

      if (alreadyExists.length < 1) {
        selectedCarriers.value = [newCarrier, ...selectedCarriers.value]
        updateJourneyTemplateWithCarrier()
      }
    },
  })

  const carrierTags = computed(() => {
    const tags: IdfTag[] = selectedCarriers.value.map(carrier => ({
      id: carrier.id.toString(),
      name: carrier.organisatieNaam,
    }))
    return tags
  })

  function removeCarrier(id: string) {
    selectedCarriers.value = selectedCarriers.value.filter(
      selectedCarrier => selectedCarrier.id.toString() !== id
    )
    updateJourneyTemplateWithCarrier()
  }

  function updateJourneyTemplateWithCarrier() {
    journeyTemplateStore.updateJourneyTemplate({
      stroomVervoerder: selectedCarriers.value,
    })
  }

  watch(
    journeyTemplate,
    () => (selectedCarriers.value = [...(journeyTemplate.value.stroomVervoerder ?? [])])
  )
</script>

<template>
  <FormHeading label="Identificatie van de vervoerder" />
  <SelectOrganization
    v-model="carrierModel"
    :carriers="selectedCarriers"
    label="Selecteer vervoerder(s)"
    required
    :error="
      selectedCarriers.length === 0 && carrierErrorKey
        ? validationMessage?.[carrierErrorKey]
        : undefined
    "
  />
  <IdfTagList :tags="carrierTags" label="vervoerder(s)" :remove-tag="removeCarrier" />
  <CarriersSelector :carriers="selectedCarriers" :remove-carrier="removeCarrier" />
</template>

<style scoped></style>
